/** @jsx jsx */

import React, { Fragment as _, useState, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import { colors } from 'styles/variables';
import { TwoColumnElement, SingleColumnElement } from '../EditorUtilities/grid';
import { Flexbox } from '@chameleoncreator/creator-ui';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import { setBlocksAction } from 'actions/setBlocksAction';
import getStore from 'services/getStore';
import CorrectAnswersWidget from './CorrectAnswersWidget';
import MultichoiceImageOptionAddRemoveWidget from './MultichoiceImageOptionAddRemoveWidget';
// updated UI
import { H1 as H1_v2, H2 } from '../EditorUtilities/UI/Headings';
import TogglePanel from '../EditorUtilities/UI/TogglePanel';
import CheckBox_v2 from '../EditorUtilities/UI/CheckBox';
import Button from '../EditorUtilities/UI/Button';
import NumberPicker_v2 from '../EditorUtilities/UI/NumberPicker';
import ColorPicker_v2 from '../EditorUtilities/UI/ColorPicker';
import Info from '../EditorUtilities/UI/Info';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import setNewBlockNewTree from 'services/apiHelpers/setNewBlockNewTree';
import pubSub from 'services/pubSub';

const { lGrey0 } = colors;

const togglePanelsCache = {};

export const ShowMultichoiceMediaFeedback = 'showMultichoiceMediaFeedback';

const EditMultichoiceImage = ({ block }: { block?: any }) => {
  //
  const {
    imageBackground = '#fff',
    imageBorderRadius = 5,
    optionTextColor,
    numberOfAttempts,
    correctOptions,
    lockDown,
    score,
    useFeedback = true,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
    randomizeOptions = false,
    useMediaInColumn = true,
    useMediaInFeedback = true,
    useUniqueOptionFeedback,
    showCorrectAnswer = true,
    showAttemptNumber = true,
    dropShadow = false,
    imageWidth = 130,
    imageHeight = 130,
    outlineColor = '#000',
    focusColor = '#000',
    selectedColor = '#000',
    outlineStyle = '1px solid transparent',
  } = block.props;

  const [{ isLive, isCorrect }, setIsLive] = useState({
    isLive: false,
    isCorrect: false,
  });

  const { id } = block;

  const scoringQuizzes = useScoringQuizzes();

  const multichoiceImageBlock = findByKeyVal_V2(
    block.id,
    'type',
    'MultichoiceImage'
  );

  // get the feedbackBlock checking if it has the Attempt feedback
  const hasAttemptFeedback = findByKeyVal_V2(block.id, 'type', 'Attempt');

  //
  const numOptions = multichoiceImageBlock!.children!.length;
  const correctOptionsToArray = correctOptions.split(/,/g);
  const multi = correctOptionsToArray.length > 1;

  const saveTogglePanelState = (panel, open) => {
    togglePanelsCache[id] = {
      ...togglePanelsCache[id],
      [panel]: open,
    };

    setBlockProps(block, { ...block.props });
  };

  window.multichoiceIsLive[id] = $ => setIsLive($);

  useEffect(() => {
    if (!useFeedback) {
      setBlockProps(block, {
        lockDown: 1,
        numberOfAttempts: 1,
      });
    }
  }, [useFeedback]);

  useEffect(
    () => () => {
      window.multichoiceIsLive[id] = null;
    },
    []
  );

  return (
    <_>
      {/* <Box mt={3}> */}
      <TogglePanel
        label={<H1_v2>interaction: settings</H1_v2>}
        toggle={
          togglePanelsCache[id]
            ? togglePanelsCache[id]['interaction: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('interaction: settings', open)}
      >
        <CheckBox_v2
          label="Lock down"
          on={!!lockDown}
          onClick={() => setBlockProps(block, { lockDown: !!lockDown ? 0 : 1 })}
        />

        <div
          // css={css({
          //   ...(scoringQuizzes.length && {
          //     opacity: 0.5,
          //     pointerEvents: 'none',
          //   }),
          // })}
          style={{
            ...(scoringQuizzes.length && {
              opacity: 0.5,
              pointerEvents: 'none',
            }),
          }}
        >
          {!!scoringQuizzes.length && (
            <div
              style={{
                margin: '0 0 5px 0',
                fontSize: 12,
                fontWeight: 500,
                color: 'red',
              }}
            >
              Scoring is disabled for this interaction while an assessment is
              scoring.
            </div>
          )}
          <CheckBox_v2
            label="Score"
            on={!!score}
            onClick={() => setBlockProps(block, { score: !score })}
          />
        </div>
      </TogglePanel>

      <TogglePanel
        label={<H1_v2>multichoice: settings</H1_v2>}
        toggle={
          togglePanelsCache[id]
            ? togglePanelsCache[id]['multichoice: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('multichoice: settings', open)}
      >
        <div>
          <Info
            info="Click '+' to add a new answer. Click the number of an answer
        you wish to remove."
            style={{ margin: '0 0 5px 0' }}
          >
            Answers
          </Info>

          <MultichoiceImageOptionAddRemoveWidget
            multichoiceImageBlock={multichoiceImageBlock}
            correctOptions={correctOptions}
            updateCorrectOptions={(add, newTree, newBlock) => {
              const parsedCorrectOptions = correctOptions
                .split(/,/g)
                .filter(entry => entry !== '')
                .sort();
              const lastCorrectOption = Number(
                parsedCorrectOptions[parsedCorrectOptions.length - 1]
              );
              const numOptions = multichoiceImageBlock!.children!.length;

              if (!add && lastCorrectOption === numOptions) {
                // remove the last option from correctOptions
                parsedCorrectOptions.splice(
                  parsedCorrectOptions.indexOf(lastCorrectOption),
                  1
                );

                setBlockProps(block, {
                  correctOptions: parsedCorrectOptions.sort().join(),
                });
              }

              const store = getStore();

              setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
            }}
          />
        </div>

        <div>
          <Info
            info="Select 1 or more correct answers"
            style={{ margin: '0 0 5px 0' }}
          >
            Correct answers
          </Info>

          <CorrectAnswersWidget
            numOptions={numOptions}
            correctOptions={correctOptions}
            onUpdate={val => {
              // reset selected options
              isLive && window.showFeedback[id](null);

              // just hacking this in to keep the current format. will update
              const parsedVal = val.toString();
              const parsedCorrectOptions = correctOptions
                .split(/,/g)
                .filter(entry => entry !== '')
                .sort();

              const updatedCorrectOptions = parsedCorrectOptions.includes(
                parsedVal
              )
                ? parsedCorrectOptions.filter(option => option !== parsedVal)
                : [...parsedCorrectOptions, val];

              if (!!!updatedCorrectOptions.length)
                return alert('At least one option must be selected.');

              setBlockProps(block, {
                correctOptions: updatedCorrectOptions.sort().join(),
                ...(updatedCorrectOptions.length > 1 && {
                  useUniqueOptionFeedback: false,
                }),
              });
            }}
          />
        </div>

        <div>
          <CheckBox_v2
            label="Randomise answers"
            on={randomizeOptions}
            onClick={() =>
              setBlockProps(block, {
                randomizeOptions: !randomizeOptions,
              })
            }
          />
        </div>

        <div>
          <CheckBox_v2
            label="Include feedback"
            on={useFeedback ? 1 : 0}
            onClick={() =>
              setBlockProps(block, {
                useFeedback: !useFeedback,
              })
            }
            // style={{
            //   ...(multi && { opacity: 0.25, pointerEvents: 'none' }),
            // }}
          />
        </div>

        {useFeedback && (
          <_>
            <H1_v2>multichoice feedback</H1_v2>
            <div
              css={css`
                display: flex;
                width: 100%;
              `}
            >
              {multi || !useUniqueOptionFeedback ? (
                <TwoColumnElement>
                  <Button
                    enabled={true}
                    onClick={e => {
                      // window.showFeedback[id](true)
                      pubSub.publish(`${ShowMultichoiceMediaFeedback}${id}`, {
                        result: 'correct',
                      });
                    }}
                  >
                    Show correct
                  </Button>

                  <Button
                    enabled={true}
                    onClick={e => {
                      //window.showFeedback[id](false)
                      pubSub.publish(`${ShowMultichoiceMediaFeedback}${id}`, {
                        result: 'incorrect',
                      });
                    }}
                  >
                    Show incorrect
                  </Button>
                </TwoColumnElement>
              ) : (
                <SingleColumnElement>
                  <Flexbox
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      ...(!isLive && { opacity: 0.25, pointerEvents: 'none' }),
                    }}
                  >
                    <Button
                      enabled={true}
                      onClick={e => {
                        // window.showFeedback[id](isCorrect)
                        pubSub.publish(`${ShowMultichoiceMediaFeedback}${id}`, {
                          result: 'correct',
                        });
                      }}
                    >
                      Show feedback
                    </Button>
                  </Flexbox>
                </SingleColumnElement>
              )}
            </div>

            <CheckBox_v2
              label="Use unique feedback"
              on={useUniqueOptionFeedback ? 1 : 0}
              onClick={() =>
                setBlockProps(block, {
                  useUniqueOptionFeedback: !useUniqueOptionFeedback,
                })
              }
              style={{
                ...(multi && { opacity: 0.25, pointerEvents: 'none' }),
              }}
            />

            <div
              css={css`
                margin: 10px 0 ${useUniqueOptionFeedback ? '10px' : '0'} 0;
              `}
            >
              {hasAttemptFeedback && (
                <NumberPicker_v2
                  label="Number of attempts"
                  min={1}
                  value={numberOfAttempts}
                  onChange={value => {
                    setBlockProps(block, { numberOfAttempts: value });
                  }}
                />
              )}
            </div>

            {hasAttemptFeedback &&
            !useUniqueOptionFeedback &&
            numberOfAttempts > 1 ? (
              <_>
                <SingleColumnElement>
                  <Flexbox>
                    <Button
                      enabled={true}
                      onClick={e => {
                        //window.showFeedback[id]('attempt')
                        pubSub.publish(`${ShowMultichoiceMediaFeedback}${id}`, {
                          result: 'attempt',
                        });
                      }}
                    >
                      Show attempted
                    </Button>
                  </Flexbox>
                </SingleColumnElement>
              </_>
            ) : null}

            {hasAttemptFeedback && numberOfAttempts > 1 && (
              <CheckBox_v2
                label="Show attempt number"
                on={showAttemptNumber ? 1 : 0}
                onClick={() =>
                  setBlockProps(block, {
                    showAttemptNumber: !showAttemptNumber,
                  })
                }
              />
            )}

            <div
              css={css`
                margin: 10px 0 0 0;
              `}
            >
              <CheckBox_v2
                label="Include media in feedback"
                on={useMediaInFeedback ? 1 : 0}
                onClick={() =>
                  setBlockProps(block, {
                    useMediaInFeedback: !useMediaInFeedback,
                  })
                }
              />
            </div>
          </_>
        )}

        <CheckBox_v2
          label="Show tick on completion"
          on={showCorrectAnswer ? 1 : 0}
          onClick={() =>
            setBlockProps(block, {
              showCorrectAnswer: !!showCorrectAnswer ? 0 : 1,
            })
          }
        />

        <div>
          <Info
            info="Control how you would like the interaction to progress when resolved."
            style={{ margin: '0 0 5px 0' }}
          >
            Navigation
          </Info>

          <div>
            <CheckBox_v2
              label="Remain on page when answered correct"
              on={navigationBehaviour.correct ? 1 : 0}
              style={{
                margin: '0 0 10px 0',
              }}
              onClick={() =>
                setBlockProps(block, {
                  navigationBehaviour: {
                    ...navigationBehaviour,
                    correct: !navigationBehaviour.correct,
                  },
                })
              }
            />

            <CheckBox_v2
              label="Remain on page when answered incorrect"
              on={navigationBehaviour.incorrect ? 1 : 0}
              style={{
                margin: '0 0 10px 0',
              }}
              onClick={() =>
                setBlockProps(block, {
                  navigationBehaviour: {
                    ...navigationBehaviour,
                    incorrect: !navigationBehaviour.incorrect,
                  },
                })
              }
            />
          </div>
        </div>

        <H1_v2>multichoice appearance: settings</H1_v2>

        <div
          css={css`
            margin: 10px 0 0 0;
          `}
        >
          <CheckBox_v2
            label="Include media"
            on={useMediaInColumn ? 1 : 0}
            onClick={() =>
              setBlockProps(block, {
                useMediaInColumn: !useMediaInColumn,
              })
            }
          />
        </div>

        <NumberPicker_v2
          label="Image width"
          min={0}
          value={imageWidth}
          onChange={value => {
            setBlockProps(block, { imageWidth: value });
          }}
        />

        <NumberPicker_v2
          label="Image height"
          min={0}
          value={imageHeight}
          onChange={value => {
            setBlockProps(block, { imageHeight: value });
          }}
        />

        <ColorPicker_v2
          inColor={imageBackground}
          outColor={color => setBlockProps(block, { imageBackground: color })}
          label="Image background colour"
          clearedColor=""
        />

        <ColorPicker_v2
          inColor={optionTextColor}
          outColor={color =>
            setBlockProps(block, {
              optionTextColor: color,
            })
          }
          label="Text colour"
          clearedColor="#000"
        />

        <CheckBox_v2
          label="Drop shadow"
          on={dropShadow ? 1 : 0}
          onClick={() =>
            setBlockProps(block, {
              dropShadow: !dropShadow,
            })
          }
        />

        <NumberPicker_v2
          label="Image border radius"
          min={0}
          value={imageBorderRadius}
          onChange={value => {
            setBlockProps(block, { imageBorderRadius: value });
          }}
        />

        <ColorPicker_v2
          inColor={outlineColor}
          outColor={color =>
            setBlockProps(block, {
              outlineColor: color,
            })
          }
          label="Outline colour"
          clearedColor="#000"
        />

        <ColorPicker_v2
          inColor={focusColor}
          outColor={color =>
            setBlockProps(block, {
              focusColor: color,
            })
          }
          label="Focus colour"
          clearedColor="#000"
        />

        <ColorPicker_v2
          inColor={selectedColor}
          outColor={color =>
            setBlockProps(block, {
              selectedColor: color,
            })
          }
          label="Selected colour"
          clearedColor="#000"
        />
      </TogglePanel>
      {/* </Box> */}
    </_>
  );
};

export default EditMultichoiceImage;
